import {
  Card,
  Col,
  Flex,
  Form,
  FormInstance,
  message,
  Row,
  Select,
  Space,
  Spin,
} from 'antd';
import { isValidPhoneNumber } from 'libphonenumber-js';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import PhoneInput from 'react-phone-input-2';
import type { ErrorLike, LocationData } from '@/apis/types';
import {
  GetLocationsWithPaginationAndSearch,
  GetProducts,
  ProductData,
  SendNotification,
} from '../../../apis/onboarding';
import alertIcon from '../../../assets/images/alertGroup.svg';
import emptyState from '../../../assets/images/emptyState.svg';
import {
  ClockIcon,
  LocationIcon,
  TruckIcon,
  type IconProps,
} from '../../../assets/icons/icons';
import { useToken } from '../../../common/helper';
import CheckboxSelector from '../../../components/CheckBoxSelector';
import CustomInput from '../../../components/CustomInput';
import CustomParagraph from '../../../components/CustomParagraph';
import CustomSelect from '../../../components/CustomSelect';
import CustomTitle from '../../../components/CustomTitle';
import { useSpin } from '../../../components/SpinContext';
import useUnauthorize from '../../../hooks/useUnauthorize';
import FrequencySelectorHorizontal, {
  type FrequencyType,
} from './FrequencySelectorHorizontal';
import StepsHeading from './StepsHeading';

type NotificationTypeId =
  'CRITICAL_ALERT' | 'PICKUP_DELIVERY' | 'AT_LOCATION' | 'ETA_BASED';
type NotificationMethod = 'MOBILE' | 'EMAIL' | 'SLACK' | 'TEAMS' | 'WHATSAPP';

interface NotificationType {
  id: NotificationTypeId;
  label: string;
  /** Image icon, used when no shared icon component is given. */
  icon?: string;
  Icon?: React.ComponentType<IconProps>;
  description: string;
}

interface NotificationSetupProps {
  /** Callback triggered when notification is successfully created */
  onNext: (payload: unknown) => void;
  /** Controls drawer open state for resetting form */
  drawerOpen?: boolean;
  /** Ant Design form instance */
  form: FormInstance;
}

export interface NotificationSetupRef {
  /** Submits the form and creates notification */
  completeStep: () => Promise<boolean>;
}

/**
 * Notification setup component for onboarding flow.
 * Allows users to configure their first notification preferences.
 *
 * @param onNext - Callback when notification is created
 * @param drawerOpen - Controls drawer state
 * @param form - Ant Design form instance
 */
const NotificationSetup = forwardRef<
  NotificationSetupRef,
  NotificationSetupProps
>(({ onNext, drawerOpen, form }, ref) => {
  const { decodedUserToken } = useToken();
  const usermail = decodedUserToken()?.email;
  const [productList, setProductList] = useState<ProductData[]>([]);
  const [selectedOption, setSelectedOption] =
    useState<NotificationMethod | null>(null);
  const [step, setStep] = useState<number>(1);
  const [selectedType, setSelectedType] = useState<NotificationType | null>(
    null
  );
  const { showSpin, hideSpin } = useSpin();
  const { token } = useToken();
  const authToken = token ?? '';
  const { unauthorize } = useUnauthorize();
  const [selectedFrequency, setSelectedFrequency] =
    useState<FrequencyType>(null);
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [selectedUserGroup, setSelectedUserGroup] = useState<string[]>([]);
  const changedByList = [
    { value: '15 minutes', label: '15 minutes' },
    { value: '30 minutes', label: '30 minutes' },
    { value: '1 hour', label: '1 hour' },
    { value: 'Over 2 hours', label: 'Over 2 hours' },
  ];

  const notificationTypes: NotificationType[] = [
    {
      id: 'CRITICAL_ALERT' as const,
      label: 'Critical / At-Risk Alert',
      icon: alertIcon,
      description:
        'An alert noting an issue that has potentially or could lead to a compromised product',
    },
    {
      id: 'PICKUP_DELIVERY' as const,
      label: 'Pickup / Delivery Confirmation',
      Icon: TruckIcon,
      description:
        'Notification when there has been a successful pickup or delivery',
    },
    {
      id: 'AT_LOCATION' as const,
      label: 'Location',
      Icon: LocationIcon,
      description:
        'Notification when a product is at a specific location (e.g., a warehouse)',
    },
    {
      id: 'ETA_BASED' as const,
      label: 'ETA',
      Icon: ClockIcon,
      description: 'Notification when projected ETA moves by a set time',
    },
  ];
  useImperativeHandle(ref, () => ({
    completeStep: handleSubmit,
  }));
  const handleUserGroupChange = (newValues: string[]) => {
    setSelectedUserGroup(newValues);
  };

  const handleTypeSelect = (id: NotificationTypeId) => {
    const selected = notificationTypes.find((t) => t.id === id);
    setSelectedType(selected ?? null);
    setStep(2);
    form.resetFields();
    setSelectedProducts([]);
    setSelectedOption(null);
    setSelectedFrequency(null);
    setSelectedUserGroup([]);
  };

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [locationsList, setLocationsList] = useState<LocationData[]>([]);

  const fetchLocations = useCallback(
    (checked: boolean, page: number, search: string, isLoadMore = false) => {
      setIsFetchingMore(true);
      GetLocationsWithPaginationAndSearch(authToken, checked, page, search)
        .then((res) => {
          const data = res?.data?.data;
          if (data?.is_success) {
            const newItems = data?.data?.data || [];

            setLocationsList((prev) =>
              isLoadMore ? [...prev, ...newItems] : newItems
            );

            setTotalPages(data?.page_count);
          } else {
            message.error(data?.message || 'Failed to fetch locations');
          }
        })
        .catch((error) => {
          unauthorize(error);
          message.error('Failed to fetch locations');
        })
        .finally(() => {
          hideSpin();
          setIsFetchingMore(false);
        });
    },
    [hideSpin, authToken, unauthorize]
  );

  useEffect(() => {
    setPage(1);
    fetchLocations(false, 1, '', false);
  }, [fetchLocations]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    const reachedBottom =
      target.scrollTop + target.offsetHeight >= target.scrollHeight - 10;
    if (reachedBottom && !isFetchingMore && page < totalPages) {
      const next = page + 1;
      setPage(next);
      fetchLocations(false, next, '', true);
    }
  };

  const renderNotificationCard = (
    type: NotificationType,
    onClick: () => void,
    isSelected = false
  ) => (
    <Card
      key={type.id}
      className={`notification-type-card${isSelected ? ' selected' : ''}`}
      onClick={onClick}
    >
      <div className='notification-type-card-content'>
        {type.Icon ? (
          <span className='notification-type-card-icon'>
            <type.Icon />
          </span>
        ) : (
          <img
            className={`notification-type-card-icon ${
              type.id === 'CRITICAL_ALERT'
                ? 'notification-type-card-icon-critical'
                : ''
            }`}
            src={type.icon}
            alt={type.label}
            onError={(e) =>
              ((e.target as HTMLImageElement).style.display = 'none')
            }
          />
        )}
        <div className='notification-type-card-copy'>
          <CustomParagraph
            className='notification-type-card-title'
            color='var(--text-primary)'
            text={type.label}
            fontSize='14px'
            fontWeight='600'
            marginBottom={5}
          />
          <CustomParagraph
            className='notification-type-card-description'
            color='var(--text-light)'
            text={type.description}
            fontSize='12px'
            marginBottom={0}
          />
        </div>
      </div>
    </Card>
  );
  const renderTypeSpecificContent = (typeId: NotificationTypeId) => {
    switch (typeId) {
      case 'CRITICAL_ALERT':
        return (
          <Form.Item>
            <CustomTitle
              text='Alert type'
              fontSize='14px'
              fontWeight='600'
              lineHeight='16px'
              color='black'
              marginBottom='10px'
            />
            <CheckboxSelector
              selectedValues={selectedUserGroup}
              onChange={handleUserGroupChange}
              options={[
                {
                  value: 'Critical',
                  label: 'Critical alert',
                  description:
                    'An alert that is indicative of potential product compromise',
                },
                {
                  value: 'At-risk',
                  label: 'At-risk alert',
                  description:
                    'An alert that warns of a potential issue that may escalate into a critical alert',
                },
              ]}
            />
          </Form.Item>
        );

      case 'PICKUP_DELIVERY':
        return (
          <Form.Item>
            <CustomTitle
              text='Type'
              fontSize='14px'
              fontWeight='600'
              lineHeight='16px'
              color='black'
              marginBottom='10px'
            />
            <CheckboxSelector
              selectedValues={selectedUserGroup}
              onChange={handleUserGroupChange}
              options={[
                {
                  value: 'pickup',
                  label: 'Pickup',
                  description:
                    'Notification when there has been a successful pickup',
                },
                {
                  value: 'delivery',
                  label: 'Delivery',
                  description:
                    'Notification when there has been a successful delivery',
                },
              ]}
            />
          </Form.Item>
        );
      case 'AT_LOCATION':
        return (
          <Col span={24} style={{ marginTop: '20px' }}>
            <CustomTitle
              text='Location'
              fontSize='14px'
              fontWeight='600'
              lineHeight='16px'
              color='black'
              marginBottom='5px'
            />

            <Form.Item
              name='facility_type'
              rules={[{ message: 'Select a type' }]}
            >
              <CustomSelect
                width='100%'
                placeholder='Select...'
                options={
                  Array.isArray(locationsList)
                    ? locationsList.map((product) => ({
                        value: product?.id,
                        label: product?.name,
                      }))
                    : []
                }
                onPopupScroll={handleScroll}
                bgColor='var(--light-gray)'
                textColor='var(--text-secondary)'
                borderRadius='8px'
                height='36px'
                border='none'
                placeholderColor='var(--text-secondary)'
                dropdownRender={(menu: React.ReactNode) => (
                  <>
                    {menu}
                    {isFetchingMore && (
                      <Flex justify='center' className='py-3'>
                        <Spin size='small' />
                      </Flex>
                    )}
                  </>
                )}
              />
            </Form.Item>
          </Col>
        );

      case 'ETA_BASED':
        return (
          <Col span={24} style={{ marginTop: '20px' }}>
            <CustomTitle
              text='Changed by'
              fontSize='14px'
              fontWeight='600'
              lineHeight='16px'
              color='black'
              marginBottom='5px'
            />
            <Form.Item name='changed_by' rules={[{ message: 'Select a type' }]}>
              <CustomSelect
                width='100%'
                placeholder='Select...'
                options={changedByList}
                bgColor='var(--light-gray)'
                textColor='var(--text-secondary)'
                borderRadius='8px'
                height='36px'
                border='none'
                placeholderColor='var(--text-secondary)'
              />
            </Form.Item>
          </Col>
        );
      default:
        return null;
    }
  };

  const handleSelectChange = (value: NotificationMethod) => {
    setSelectedOption(value);
    if (value === 'MOBILE' || value === 'WHATSAPP') {
      form.setFieldsValue({ notification_channel: '' });
    } else {
      form.setFieldsValue({ notification_channel: usermail });
    }
  };

  const fetchProductLists = useCallback(() => {
    showSpin();
    GetProducts(authToken)
      .then((res) => {
        const data = res?.data ? res.data : null;
        if (data) {
          if (data?.data?.is_success) {
            const res = data.data.data;
            if (res) {
              setProductList(res);
            }
          } else {
            message.error(data?.data?.message);
          }
        }
      })
      .catch((error) => {
        unauthorize(error);
      })
      .finally(() => {
        hideSpin();
      });
  }, [hideSpin, showSpin, authToken, unauthorize]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (!selectedType) {
        message.error('Please select a notification type.');
        return false;
      }

      if (
        (selectedType?.id === 'CRITICAL_ALERT' ||
          selectedType?.id === 'PICKUP_DELIVERY') &&
        !selectedUserGroup
      ) {
        message.error('Please select type');
        return false;
      }

      const isSpecialType =
        selectedType?.id === 'ETA_BASED' || selectedType?.id === 'AT_LOCATION';

      const payload = {
        alert_type: selectedType?.id,
        is_critical: isSpecialType
          ? false
          : selectedUserGroup.includes('Critical'),
        is_at_risk: isSpecialType
          ? false
          : selectedUserGroup.includes('At-risk'),
        is_pickup_confirmation: isSpecialType
          ? false
          : selectedUserGroup.includes('pickup'),
        is_delivery_confirmation: isSpecialType
          ? false
          : selectedUserGroup.includes('delivery'),
        location: values.facility_type || null,
        change_by: values.changed_by || null,
        products: values.products.includes('all_products')
          ? productList.map((product) => product.id)
          : values.products || [],
        frequency: values.frequency,
        time_1: values.send_at_1 ? values.send_at_1.format('h:mma') : null,
        time_2: values.send_at_2 ? values.send_at_2.format('h:mma') : null,
        day: values.day_of_week || null,
        notification_method: values.notification_method,
        notification_channel: values.notification_channel,
        // @ts-expect-error (not a property on decodedUserToken, but was present in the original JSX)
        created_by: decodedUserToken()?.id,
        create_self: true,
        // @ts-expect-error (not a property on decodedUserToken, but was present in the original JSX)
        users: [decodedUserToken()?.company_user_id],
      };

      showSpin();
      try {
        const res = await SendNotification(authToken, payload);
        const data = res?.data || null;
        if (data?.data?.is_success) {
          form.resetFields();
          message.success(data?.data?.message);
          onNext(payload);
          return true;
        } else {
          message.error(data?.data?.message);
          return false;
        }
      } catch (error) {
        unauthorize(error as ErrorLike);
        return false;
      } finally {
        hideSpin();
      }
    } catch (error) {
      console.error('Validation or payload prep failed:', error);
      return false;
    }
  };

  useEffect(() => {
    if (usermail) {
      form.setFieldsValue({
        notification_channel: usermail,
      });
    }
  }, [form, usermail]);

  useEffect(() => {
    fetchProductLists();
  }, [fetchProductLists]);

  useEffect(() => {
    setStep(1);
  }, [drawerOpen]);

  useEffect(() => {
    if (step === 1) {
      form.resetFields();
      setSelectedOption(null);
    }
  }, [form, step]);

  useEffect(() => {
    if (drawerOpen) {
      form.resetFields();
      setSelectedOption(null);
      setSelectedType(null);
    }
  }, [drawerOpen, form]);

  return (
    <Form
      className='custom-form form-item onboarding-notifications-step'
      form={form}
      onFinish={handleSubmit}
      layout='vertical'
      style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
    >
      <div className='large-steps-content'>
        <StepsHeading
          heading='Tell us how you’d like to receive notifications'
          text='Finally, let’s set your first notification so you can be notified about key shipment events. More advanced options are available in Settings.'
        />

        <div className='notification-setup-layout'>
          <div className='notification-setup-pane notification-setup-type-pane'>
            <div className='custom-scrollbar notification-setup-scroll'>
              <div className='custom-card'>
                <CustomParagraph
                  marginBottom={'5px'}
                  text='Choose a notification type'
                  fontSize='18px'
                  fontWeight={700}
                />
                <Space direction='vertical' size={12} style={{ width: '100%' }}>
                  {notificationTypes.map((type) =>
                    renderNotificationCard(
                      type,
                      () => handleTypeSelect(type.id),
                      selectedType?.id === type.id
                    )
                  )}
                </Space>
              </div>
            </div>
          </div>

          <div className='notification-setup-divider' />

          <div className='notification-setup-pane notification-setup-config-pane'>
            {selectedType ? (
              <div className='custom-scrollbar notification-setup-scroll'>
                <div className='custom-card'>
                  <div className='notification-type-content'>
                    {renderTypeSpecificContent(selectedType?.id)}
                  </div>
                </div>

                <Col span={24}>
                  <Form.Item
                    className='custom-input form-input-style'
                    label='Products to receive notifications for'
                    name='products'
                    style={{ marginBottom: '0px' }}
                    rules={[
                      {
                        required: true,
                        message: 'Please select at least one product',
                      },
                    ]}
                  >
                    <Select
                      className='gray-tag-select'
                      showSearch={false}
                      mode='multiple'
                      placeholder='Select...'
                      optionLabelProp='label'
                      value={selectedProducts}
                      defaultValue={selectedProducts}
                      onChange={(value) => {
                        if (value.includes('all_products')) {
                          setSelectedProducts(['all_products']);
                          form.setFieldsValue({ products: ['all_products'] });
                        } else {
                          setSelectedProducts(value);
                          form.setFieldsValue({ products: value });
                        }
                      }}
                      style={{
                        minHeight: '36px',
                        border: '0px',
                        backgroundColor: 'var(--light-gray)',
                        color: 'var(--text-secondary)',
                        borderRadius: '8px',
                        lineHeight: '38px',
                        width: '100%',
                      }}
                    >
                      {Array.isArray(productList) && productList.length > 0 && (
                        <Select.Option
                          value='all_products'
                          label='All Products'
                        >
                          All Products
                        </Select.Option>
                      )}

                      {Array.isArray(productList) &&
                        productList.map((product) => (
                          <Select.Option
                            key={product.id}
                            value={product.id}
                            label={product.name}
                            disabled={selectedProducts.includes('all_products')}
                          >
                            {product.name}
                          </Select.Option>
                        ))}
                    </Select>
                  </Form.Item>
                </Col>

                <FrequencySelectorHorizontal
                  setSelectedFrequency={setSelectedFrequency}
                  selectedFrequency={selectedFrequency}
                />

                <Row style={{ width: '100%' }}>
                  <Col span={24}>
                    <Form.Item
                      className='custom-input label-14px'
                      label='Notify via'
                      name='notification_method'
                      rules={[
                        {
                          required: true,
                          message: 'Please select a notification method',
                        },
                      ]}
                    >
                      <CustomSelect
                        placeholder='Select'
                        onChange={handleSelectChange}
                        width='100%'
                        height='36px'
                        border='0px'
                        bgColor='var(--light-gray)'
                        textColor='var(--text-secondary)'
                        borderRadius='8px'
                        placeholderColor='var(--text-light)'
                        options={[
                          { value: 'MOBILE', label: 'SMS' },
                          { value: 'EMAIL', label: 'Email' },
                          { value: 'SLACK', label: 'Slack' },
                          { value: 'TEAMS', label: 'Teams' },
                          { value: 'WHATSAPP', label: 'WhatsApp' },
                        ]}
                      />
                    </Form.Item>
                  </Col>

                  {selectedOption && (
                    <Col span={24}>
                      <CustomTitle
                        text={
                          selectedOption === 'MOBILE' ||
                          selectedOption === 'WHATSAPP'
                            ? 'Your Phone number'
                            : 'Email'
                        }
                        fontSize='12px'
                        fontWeight='600'
                        lineHeight='16px'
                        color='black'
                      />
                      <Form.Item
                        name='notification_channel'
                        className='custom-input label-14px'
                        rules={
                          selectedOption === 'EMAIL'
                            ? []
                            : [
                                ...(selectedOption === 'WHATSAPP' ||
                                selectedOption === 'MOBILE'
                                  ? [
                                      {
                                        validator: (
                                          _: unknown,
                                          value: string
                                        ) => {
                                          if (!value || value.trim() === '') {
                                            return Promise.reject(
                                              new Error(
                                                `Please enter ${selectedOption.toLowerCase()} number`
                                              )
                                            );
                                          }
                                          try {
                                            if (isValidPhoneNumber(value)) {
                                              return Promise.resolve();
                                            } else {
                                              return Promise.reject(
                                                new Error(
                                                  'Invalid phone number'
                                                )
                                              );
                                            }
                                          } catch {
                                            return Promise.reject(
                                              new Error('Invalid phone number')
                                            );
                                          }
                                        },
                                      },
                                    ]
                                  : [
                                      {
                                        required: true,
                                        message: 'Please enter value',
                                      },
                                    ]),
                              ]
                        }
                      >
                        {selectedOption === 'MOBILE' ||
                        selectedOption === 'WHATSAPP' ? (
                          <PhoneInput
                            country='us'
                            value={form.getFieldValue('notification_channel')}
                            onChange={(value, countryData, e, formattedValue) =>
                              form.setFieldsValue({
                                notification_channel: formattedValue,
                              })
                            }
                            inputStyle={{
                              width: '100%',
                              height: '36px',
                              background: 'var(--light-gray)',
                              borderRadius: '8px',
                              border: 'none',
                              color: 'var(--text-secondary)',
                            }}
                          />
                        ) : (
                          <CustomInput
                            value={form.getFieldValue('notification_channel')}
                            disabled
                            type='text'
                            placeholder={usermail}
                            height='36px'
                            border='2px solid #E8E8E8'
                            width='100%'
                            bgColor='var(--surface-container)'
                            textColor='var(--text-secondary)'
                            borderRadius='8px'
                            placeholderColor='var(--text-secondary)'
                            lineHeight='38px'
                          />
                        )}
                      </Form.Item>
                    </Col>
                  )}

                  {(selectedOption === 'TEAMS' ||
                    selectedOption === 'SLACK') && (
                    <Col span={24}>
                      <p className='notification-representative-note'>
                        A Zoomlogi representative will contact you.
                      </p>
                    </Col>
                  )}
                </Row>
              </div>
            ) : (
              <div className='notification-empty-state'>
                <img src={emptyState} alt='Notification Placeholder' />
              </div>
            )}
          </div>
        </div>
      </div>
    </Form>
  );
});
NotificationSetup.displayName = 'NotificationSetup';
export default NotificationSetup;
